/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import { Component, useEffect, useState } from 'react';
import { Layout } from 'antd';
import { withRouter, Route } from 'react-router';
import { Header } from './header';
import { SideMenu } from './side-menu';
import styles from './dashboard.styles';
import { allowedExperimental, filterBasedOnRoles, getAdminLanding, isDev, isStage, userIsVerfied } from '../actions/utils';
import { Routes } from './dashboard.routes';
import { InviteWatcher } from './invite/invitewatcher';
import { useView } from '../_shared/hooks/useView';
import { useScreen } from '../_shared/hooks/useScreen';
import { EnvSelector } from '../_shared/envselector';
import { ImpersonationBanner } from '../_shared/impersonation-banner';

export const DashboardComponent = props => {
  const [envModal, setEnvModal] = useState({
    visible: false,
    open: () => {
      setEnvModal(s => ({ ...s, visible: true }));
    },
    onCancel: () => {
      setEnvModal(s => ({ ...s, visible: false }))
    },
    onOk: () => {
      setEnvModal(s => ({ ...s, visible: false }))
    }
  })

  const switchEnv = () => {
    envModal.open();
  }

  const [state, setState] = useState({
    allRoutes: Routes,
    ellipsisRoutes: [
      Routes.account,
      Routes.changeEmail,
      Routes.changePassword,
      Routes.accountdelete,
      (isDev() || isStage()) && allowedExperimental() && {
        title: 'Switch Env',
        icon: { type: 'swap' },
        onClick: switchEnv
      },
      {
        title: 'Sign Out',
        icon: { type: 'unlock' },
        path: '/dashboard/account',
        onClick: props.logout,
      },
    ].filter(f => !!f),
    headerRoutes: [
      Routes.landing,
      Routes.admin
    ],
  });

  const { view, toggleDrawer } = useView();
  const { isMobile } = useScreen();

  useEffect(() => {
    props.retrieveData({ isMobile });
    // On initial app load only (refresh / direct nav to /dashboard), admins
    // and super admins land on the configurable admin landing (admin.landingPath,
    // default Admin Dashboard). Clicking the "Dashboard" header tab afterwards is
    // in-app navigation and is NOT redirected, so admins can still reach the
    // Shipping Dashboard.
    const role = props.sState && props.sState.user && props.sState.user.role;
    if ((role === 0 || role === 1) && props.history.location.pathname === '/dashboard') {
      props.history.replace(getAdminLanding());
    }
  }, []);

  const changeRoute = (route) => {
    if (isMobile) { toggleDrawer() };
    // The top "Admin" tab has a configurable destination (admin.landingPath);
    // every other route navigates to its own path.
    const target = route.isAdminTab ? getAdminLanding() : route.path;
    if (route.sideRoutes) {
      setState(s => ({ ...s, sideRoutes: route.sideRoutes }));
      if (target && target.indexOf('http') > -1) {
        return window.open(target, "_blank");
      }
      return props.history.push(target);
    }
    props.history.push(target);
  };
  
  const findRoute = () => {
    const { location } = props.history;
    let route;
    for (let key in Routes) {
      let r = Routes[key];
      if (r && r.path === location.pathname) {
        route = r;
        break;
      }
    }
    return route;
  }

  const { sState } = props;
  const { headerRoutes, ellipsisRoutes, allRoutes } = state;

  const isVerified = userIsVerfied(sState.user);

  const currentRoute = findRoute();

  const _headerRoutes = filterBasedOnRoles(headerRoutes, sState);
  const resolvedSideRoutes = currentRoute && (
    typeof currentRoute.sideRoutes === 'function'
      ? currentRoute.sideRoutes(sState)
      : currentRoute.sideRoutes
  );
  const _sideRoutes = !isVerified ? [] : filterBasedOnRoles(resolvedSideRoutes, sState);
  const _ellipsisRoutes = filterBasedOnRoles(ellipsisRoutes, sState);

  const showDrawer = _sideRoutes.length > 0 && (isMobile ? view.drawerOpen : true);

  return (
    <Layout>
      <ImpersonationBanner />
      <Header
        routes={_headerRoutes}
        ellipsisRoutes={_ellipsisRoutes}
        changeRoute={changeRoute}
        currentRoute={currentRoute}
      />
      <Layout>
        {showDrawer && (
          <SideMenu
            routes={_sideRoutes}
            currentRoute={currentRoute}
            changeRoute={changeRoute}
          />
        )}
        <Layout>
          <Layout.Content
            id={'ContentContainer'}
            css={css(styles.contentContainer)}
          >
            {Object.keys(allRoutes).map((key, i) => (
              <Route key={i} {...allRoutes[key]} />
            ))}
          </Layout.Content>
        </Layout>
      </Layout>
      <InviteWatcher />
      <EnvSelector {...envModal} />
    </Layout>
  );
}

export const Dashboard = withRouter(DashboardComponent);
